import DigitalCreditCardFlyoutSvg from 'ext-ui/svg/illustration/DigitalCreditCardFlyoutSvg'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { RequestVirtualCardScreen } from 'src/app/RequestVirtualCard/RequestVirtualCard'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { VirtualCardTypes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserAssociation } from 'src/models/user.models'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { buildLowLimitAlert } from 'src/utils/lowLimitAlert.utils'

interface RequestVirtualCardIssuerCycleContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  postVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
  ) => void
  setScreen: (val: RequestVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  showSoftAlert?: boolean
}

const RequestVirtualCardIssuerCycleContent: React.FC<
  RequestVirtualCardIssuerCycleContentProps
> = (props) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const lowBalanceAlertFeatureEnabled = !!useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_low_balance_alert

  // Request form does NOT show funding source or "Resets on" per Figma design.
  // The requester only sees Monthly Credit Limit and Active Until.

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
  ): void => {
    // Integer-only validation: issuer-cycle cards do not support decimal cent amounts.
    if (
      flatFields.balanceDollars !== undefined &&
      Math.floor(flatFields.balanceDollars) !== flatFields.balanceDollars
    ) {
      // Non-integer amount entered — bail without submitting.
      // The AmountInputField allows decimals via typing, so we guard at submit time.
      return
    }

    props.postVirtualCardAndOpenToastAndCloseFlyout({
      cardholder:
        flatFields.userEmails !== undefined
          ? flatFields.userEmails[0]
          : undefined,
      displayName: flatFields.displayName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : undefined,
      currency: flatFields.currency,
      notes: flatFields.notes,
      validTo:
        flatFields.validToMoment !== undefined
          ? flatFields.validToMoment.format('YYYY-MM-DD')
          : undefined,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      issuerCycle: true,
      lowLimitAlert: buildLowLimitAlert(
        flatFields,
        lowBalanceAlertFeatureEnabled,
      ),
    })

    analytics?.track('Successfully request virtual card', {
      type_of_card: VirtualCardTypes.issuerCycle,
      card_name: flatFields.displayName,
      credit_limit: flatFields.balanceDollars,
      add_expense_category: Object.keys(expenseDetails).length > 0,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      add_attachments:
        props.receiptAttachments && props.receiptAttachments.length > 0,
      add_notes: !!flatFields.notes,
    })
  }

  return (
    <ActionRightFlyoutContent
      title={<>{translate('Request Recurring Card')}</>}
      specificElements={{
        imageComponent: <DigitalCreditCardFlyoutSvg />,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: <>{translate('Change Card Type')}</>,
          onChangeClick: (): void => {
            props.setScreen(RequestVirtualCardScreen.Pick)
          },
        },
      }}
    >
      <VirtualCardForm
        id={'request'}
        buttonText={<>{translate('Request Card')}</>}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingVirtualCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={<>{translate('Request From')}</>}
        recipientNamesEditable={false}
        recurrenceEditable={false}
        notesEditable
        amountProps={{
          amountEditable: true,
          amountPlaceholder: translate('Monthly Credit Limit'),
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        attachmentsProps={{
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        suggestedProps={{
          showSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.virtualCardFrom]}
        isRequest
        unableToAddNewUser
        typeofCard={VirtualCardTypes.issuerCycle}
        showCardApprovalSoftAlert={props.showSoftAlert}
        resetsOnHelperText={
          <span>
            {translate(
              'The credit limit resets to this amount on the cycle end date.',
            )}
          </span>
        }
      />
    </ActionRightFlyoutContent>
  )
}

export default RequestVirtualCardIssuerCycleContent
